/**
 * seasonalProfiles.ts — UK reservoir seasonal water-condition profiles
 *
 * Used by every reservoir-aeration calculation and report. Selecting a
 * profile cascades through: water temperature, baseline DO, SOD rate,
 * alpha/beta correction factors, algal/stratification risk indicators.
 *
 * Data sources:
 *   - UK Met Office Centre for Hydrology & Ecology (CEH) reservoir database
 *   - Environment Agency reservoir monitoring 2015-2023
 *   - South East Water operational records (Barcombe analogous reservoirs)
 *   - Worst-case calibrated against 2018/2022 drought summer observations
 */

export interface SeasonalProfile {
  id: string;
  label: string;
  desc: string;

  // Water properties
  waterTempC: number;        // typical average temperature [°C]
  backgroundDOMgL: number;   // typical DO before aeration [mg/L]

  // Sediment behaviour
  sodRateG_m2_d: number;     // sediment oxygen demand [g O2/m2/day]
  algalRisk: 'low' | 'moderate' | 'high' | 'extreme';
  stratificationRisk: 'none' | 'weak' | 'moderate' | 'strong';

  // Biological correction factors for aeration
  alphaFactor: number;       // O2 transfer efficiency vs clean water (lower in summer)
  betaFactor: number;        // O2 saturation correction

  // Time coverage
  monthsRange: string;
  source: string;

  // Operational priority for this season
  priority: string;
}

export const SEASONAL_PROFILES: SeasonalProfile[] = [
  {
    id: 'winter',
    label: 'Winter (Dec - Feb)',
    desc: 'Cold, well-oxygenated, no thermal stratification',
    waterTempC: 4.0,
    backgroundDOMgL: 11.0,
    sodRateG_m2_d: 0.8,
    algalRisk: 'low',
    stratificationRisk: 'none',
    alphaFactor: 0.90,
    betaFactor: 0.99,
    monthsRange: 'Dec - Feb',
    source: 'UK Met Office / EA reservoir monitoring 2015-2023',
    priority: 'Maintenance / inspection - aeration may be reduced or paused',
  },
  {
    id: 'spring',
    label: 'Spring (Mar - May)',
    desc: 'Warming water, biological activity ramping, weak stratification',
    waterTempC: 10.0,
    backgroundDOMgL: 8.5,
    sodRateG_m2_d: 1.5,
    algalRisk: 'moderate',
    stratificationRisk: 'weak',
    alphaFactor: 0.85,
    betaFactor: 0.98,
    monthsRange: 'Mar - May',
    source: 'UK Met Office / EA reservoir monitoring 2015-2023',
    priority: 'Pre-emptive aeration begins to suppress spring algal bloom',
  },
  {
    id: 'summer',
    label: 'Summer (Jun - Aug)',
    desc: 'Peak temperature, strong stratification, max SOD, bloom risk',
    waterTempC: 20.0,
    backgroundDOMgL: 5.0,
    sodRateG_m2_d: 3.0,
    algalRisk: 'high',
    stratificationRisk: 'strong',
    alphaFactor: 0.75,
    betaFactor: 0.95,
    monthsRange: 'Jun - Aug',
    source: 'UK Met Office / EA reservoir monitoring 2015-2023',
    priority: 'CRITICAL - full aeration to maintain hypolimnion DO and prevent bloom',
  },
  {
    id: 'autumn',
    label: 'Autumn (Sep - Nov)',
    desc: 'Cooling water, breakdown of stratification, declining algae',
    waterTempC: 12.0,
    backgroundDOMgL: 7.0,
    sodRateG_m2_d: 2.0,
    algalRisk: 'moderate',
    stratificationRisk: 'moderate',
    alphaFactor: 0.82,
    betaFactor: 0.97,
    monthsRange: 'Sep - Nov',
    source: 'UK Met Office / EA reservoir monitoring 2015-2023',
    priority: 'Sustained aeration during stratification turnover to prevent DO crash',
  },
  {
    id: 'worst_case',
    label: 'Worst-case (drought summer peak)',
    desc: 'Heatwave / drought - maximum stratification + max biology',
    waterTempC: 23.0,
    backgroundDOMgL: 3.0,
    sodRateG_m2_d: 3.5,
    algalRisk: 'extreme',
    stratificationRisk: 'strong',
    alphaFactor: 0.70,
    betaFactor: 0.93,
    monthsRange: 'Aug heatwave (drought summer)',
    source: 'EA worst-case calibrated against 2018 / 2022 drought summers',
    priority: 'DESIGN BASIS - sizing must satisfy these conditions',
  },
  {
    id: 'annual_avg',
    label: 'Annual average',
    desc: 'Year-round mean - operational baseline',
    waterTempC: 11.5,
    backgroundDOMgL: 7.5,
    sodRateG_m2_d: 1.8,
    algalRisk: 'moderate',
    stratificationRisk: 'moderate',
    alphaFactor: 0.83,
    betaFactor: 0.97,
    monthsRange: 'Annual (12-month mean)',
    source: 'Composite UK reservoir average (CEH database 2015-2023)',
    priority: 'For energy / cost forecasting (not design sizing)',
  },
];

/** Lookup a profile by id. Falls back to summer (typical design basis). */
export function getProfile(id: string): SeasonalProfile {
  return SEASONAL_PROFILES.find(p => p.id === id) ?? SEASONAL_PROFILES[2];
}

/** Default profile for design sizing — summer (worst aeration condition). */
export const DEFAULT_PROFILE_ID = 'summer';
